import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from '@/shared/config/schema'
import type { TranslationResources } from './types'

interface LocaleState {
  /** Explicit user choice on this browser; `null` means "use the configured default". */
  locale: Locale | null
  /**
   * The language the backend actually served. Differs from the chosen one when that language
   * has no catalogue and the backend fell back; `null` until a catalogue has arrived.
   */
  servedLanguage: string | null
  /** The served catalogue. Empty until one arrives: the bundled English is shown meanwhile. */
  resources: TranslationResources
  /** Clears `servedLanguage`; the previous catalogue stays until the new one arrives. */
  setLocale: (locale: Locale) => void
  setCatalogue: (servedLanguage: string, resources: TranslationResources) => void
}

export const useLocaleStore = create<LocaleState>()(
  persist(
    (set, get) => ({
      locale: null,
      servedLanguage: null,
      resources: {},
      setLocale: (locale) => {
        if (get().locale === locale) return
        set({ locale, servedLanguage: null })
      },
      setCatalogue: (servedLanguage, resources) => {
        set({ servedLanguage, resources })
      },
    }),
    {
      name: 'app:locale',
      storage: createJSONStorage(() => localStorage),
      // The catalogue is persisted so a reload renders the chosen language before the network answers.
      partialize: (state) => ({
        locale: state.locale,
        servedLanguage: state.servedLanguage,
        resources: state.resources,
      }),
      // A stored locale that is no longer supported (e.g. removed `vi`) falls back to the config default.
      merge: (persisted, current) => {
        const stored = (persisted ?? {}) as Partial<
          Record<'locale' | 'servedLanguage' | 'resources', unknown>
        >
        return {
          ...current,
          locale: isLocale(stored.locale) ? stored.locale : null,
          servedLanguage: typeof stored.servedLanguage === 'string' ? stored.servedLanguage : null,
          resources: toResources(stored.resources),
        }
      },
    },
  ),
)

function toResources(value: unknown): TranslationResources {
  if (typeof value !== 'object' || value === null) return {}
  return Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string',
    ),
  )
}

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

/** User choice → configured locale → English. */
export function resolveLocale(userChoice: Locale | null, configured: Locale | undefined): Locale {
  return userChoice ?? configured ?? DEFAULT_LOCALE
}
